// Capa HTTP y lógica pura de la fase 4 de la generación del informe: la puerta
// de la emisión (qué falta para emitir), las versiones emitidas, el formato de
// salida, la vista previa y la revisión de calidad del documento.
//
// El estado vive en TanStack Query (`hooks/useEmision.ts`); acá van los tipos
// del contrato (7b/7c/7d), los fetch y las reglas que se prueban sin montar nada.
//
// NADA SE DECIDE ACÁ: qué bloquea la emisión lo dice el servidor
// (`CondicionesDeEmision`, la puerta única). Esta capa solo traduce cada
// condición a una fila legible y al lugar de la pantalla que la resuelve.

import { ApiError, apiFetch, getApiErrorMessage, traerArchivo } from "./api";
import type { FiltroDeDudas, TonoDelCarril } from "./generacion";
import { formatearFechaYHora } from "./fechas";

/* ---------------------------------------------------------------------------
   Contrato del API
--------------------------------------------------------------------------- */

export interface DetalleDeCondicion {
  tipo: string;
  id?: number | null;
  etiqueta: string;
  /** Por qué ese elemento bloquea (`sin_recurso`, `texto_fijo_vacio`…), cuando el servidor lo distingue. */
  motivo?: string;
  /** El ítem del maestro donde se corrige un ítem sin modo, recurso o contenido. */
  maestro_item_id?: number | null;
  [clave: string]: unknown;
}

export interface CondicionDeEmision {
  codigo: string;
  mensaje: string;
  cantidad: number;
  detalle: DetalleDeCondicion[];
}

export interface CondicionesDeEmision {
  puede_emitir: boolean;
  bloqueos: CondicionDeEmision[];
  advertencias: CondicionDeEmision[];
  ultimo_cambio: string | null;
  ultima_revision_de_calidad: string | null;
}

export interface Emision {
  version: number;
  emitido_en: string;
  autor: { id: number; nombre: string } | null;
  formato: { id: number; nombre: string } | null;
  paginas: number | null;
  bytes: number | null;
  hash: string;
  cantidad_de_advertencias: number;
}

export type GravedadDeObservacion = "grave" | "advierte";
export type EstadoDeObservacion = "abierta" | "corregida" | "descartada" | "ya_no_aparece";
export type OrigenDeObservacion = "determinista" | "ia";

export interface ObservacionDeCalidad {
  id: number;
  tipo: string;
  gravedad: GravedadDeObservacion;
  origen: OrigenDeObservacion;
  estado: EstadoDeObservacion;
  seccion: { item_id: number | null; titulo: string | null } | null;
  fragmento: string | null;
  fragmento_relacionado: string | null;
  explicacion: string | null;
  sugerencia: string | null;
  motivo: string | null;
  resuelta_por: { id: number; nombre: string | null } | null;
  resuelta_en: string | null;
  corrida_id: number | null;
}

export interface CorridaDeLaCalidad {
  corrida_id: number;
  estado: "en_curso" | "ok" | "error" | "abandonada";
  paso: { nombre: string; numero: number; de: number } | null;
  porcentaje: number;
  contadores: { secciones: number; deterministas: number; ia: number; descartados: number; llamadas_al_modelo: number };
  iniciada_en: string | null;
  terminada_en: string | null;
  error: { codigo: string; mensaje: string } | null;
}

export interface ResumenDeCalidad {
  abiertas: number;
  graves_abiertas: number;
  corregidas: number;
  descartadas: number;
  ya_no_aparecen: number;
}

export interface CalidadDelInforme {
  corrida: CorridaDeLaCalidad | null;
  resumen: ResumenDeCalidad;
  observaciones: ObservacionDeCalidad[];
}

/* ---------------------------------------------------------------------------
   HTTP
--------------------------------------------------------------------------- */

export const INTERVALO_DE_POLL_DE_LA_CALIDAD_MS = 3000;

/** Sondea mientras la revisión corre; al terminar TanStack apaga el intervalo. */
export function intervaloDeLaCalidad(corrida: Pick<CorridaDeLaCalidad, "estado"> | null | undefined): number | false {
  return corrida?.estado === "en_curso" ? INTERVALO_DE_POLL_DE_LA_CALIDAD_MS : false;
}

const base = (informeId: number) => `/api/informes/${informeId}`;

export async function fetchCondiciones(informeId: number): Promise<CondicionesDeEmision> {
  const r = await apiFetch<{ data: CondicionesDeEmision }>(`${base(informeId)}/emision/condiciones`);
  return r.data;
}

export async function fetchEmisiones(informeId: number): Promise<Emision[]> {
  const r = await apiFetch<{ data: Emision[] }>(`${base(informeId)}/emisiones`);
  return r.data;
}

export async function emitirRequest(informeId: number): Promise<Emision> {
  const r = await apiFetch<{ data: Emision }>(`${base(informeId)}/emisiones`, { method: "POST" });
  return r.data;
}

export async function cambiarFormatoRequest(
  informeId: number,
  formatoId: number,
): Promise<{ id: number; nombre: string }> {
  const r = await apiFetch<{ data: { formato_salida: { id: number; nombre: string } } }>(
    `${base(informeId)}/formato-salida`,
    { method: "PUT", body: JSON.stringify({ formato_salida_id: formatoId }) },
  );
  return r.data.formato_salida;
}

export async function fetchCalidad(informeId: number): Promise<CalidadDelInforme> {
  const r = await apiFetch<{ data: CalidadDelInforme }>(`${base(informeId)}/calidad`);
  return r.data;
}

export async function correrCalidadRequest(informeId: number): Promise<CalidadDelInforme> {
  const r = await apiFetch<{ data: CalidadDelInforme }>(`${base(informeId)}/calidad`, { method: "POST" });
  return r.data;
}

export async function resolverObservacionRequest(
  informeId: number,
  observacionId: number,
  estado: "abierta" | "corregida" | "descartada",
  motivo?: string,
): Promise<ObservacionDeCalidad> {
  const r = await apiFetch<{ data: ObservacionDeCalidad }>(
    `${base(informeId)}/calidad/observaciones/${observacionId}`,
    { method: "PUT", body: JSON.stringify(motivo === undefined ? { estado } : { estado, motivo }) },
  );
  return r.data;
}

/** La ruta del PDF archivado de una versión (los bytes emitidos, nunca un render nuevo). */
export const rutaDelPdfEmitido = (informeId: number, version: number) =>
  `${base(informeId)}/emisiones/${version}/pdf`;

/** El borrador se genera al pedirlo (tarda segundos): devuelve los bytes para embeberlos desde un blob. */
export async function traerBorradorRequest(informeId: number): Promise<Blob> {
  return traerArchivo(`${base(informeId)}/documento/borrador`);
}

/* ---------------------------------------------------------------------------
   Lógica pura
--------------------------------------------------------------------------- */

const plural = (n: number, singular: string, plural: string) => `${n} ${n === 1 ? singular : plural}`;

/** A dónde lleva una fila del checklist. `null`: no se sabe, la fila no navega. */
export type DestinoDeLaCondicion =
  | { tipo: "fase"; fase: 1; filtro: FiltroDeDudas; dudaId: number | null }
  | { tipo: "fase"; fase: 2 }
  | { tipo: "fase"; fase: 3; itemId: number | null }
  | { tipo: "maestro"; itemId: number | null }
  | { tipo: "calidad" };

export function destinoDeLaCondicion(condicion: CondicionDeEmision): DestinoDeLaCondicion | null {
  const primerId = condicion.detalle.find((d) => typeof d.id === "number")?.id ?? null;

  switch (condicion.codigo) {
    case "dudas_bloqueantes":
      return { tipo: "fase", fase: 1, filtro: "bloquean", dudaId: primerId };
    case "correcciones_pendientes":
      // Se ven y se cierran en el detalle de la duda respondida.
      return { tipo: "fase", fase: 1, filtro: "respondidas", dudaId: primerId };
    case "dudas_que_advierten":
      return { tipo: "fase", fase: 1, filtro: "abiertas", dudaId: null };
    case "valorizacion_pendiente":
      return { tipo: "fase", fase: 2 };
    case "secciones_sin_validar":
    case "secciones_desactualizadas":
    case "secciones_con_errores_de_verificacion":
      return { tipo: "fase", fase: 3, itemId: primerId };
    case "items_sin_modo":
    case "items_con_recurso_pendiente":
    case "items_con_contenido_pendiente":
      return {
        tipo: "maestro",
        itemId: condicion.detalle.find((d) => typeof d.maestro_item_id === "number")?.maestro_item_id ?? null,
      };
    case "observaciones_graves":
    case "observaciones_que_advierten":
    case "sin_revision_de_calidad":
    case "revision_de_calidad_desactualizada":
      return { tipo: "calidad" };
    default:
      return null;
  }
}

export type TonoDeLaFila = "ok" | "bloquea" | "aviso";

export interface FilaDelChecklist {
  clave: string;
  titulo: string;
  detalle: string;
  cantidad: number;
  tono: TonoDeLaFila;
  /** El glifo del círculo: ✕ rojo, ! ámbar, ✓ verde. */
  icono: "✕" | "!" | "✓";
  destino: DestinoDeLaCondicion | null;
  /** Lo que bloquea, uno por uno: sin esto, con cientos de ítems no hay cómo saber cuáles son. */
  elementos: ElementoDeLaFila[];
}

export interface ElementoDeLaFila {
  clave: string;
  etiqueta: string;
  /** El motivo en palabras, o `null` si el servidor no lo distingue. */
  motivo: string | null;
  destino: DestinoDeLaCondicion | null;
}

const TEXTO_DEL_MOTIVO: Record<string, string> = {
  sin_recurso: "Sin recurso",
  recurso_desconocido: "Su recurso ya no existe",
  texto_fijo_vacio: "Texto fijo vacío",
  sin_pestana: "Sin pestaña de origen",
  pestana_vacia: "La pestaña de origen está vacía",
  pestana_sin_validar: "La pestaña de origen no está validada",
};

/** Cada elemento del detalle lleva a SU lugar, no al del primero de la lista. */
function elementosDe(condicion: CondicionDeEmision): ElementoDeLaFila[] {
  return condicion.detalle.map((d, i) => ({
    clave: `${condicion.codigo}:${i}`,
    etiqueta: d.etiqueta,
    motivo: (d.motivo && TEXTO_DEL_MOTIVO[d.motivo]) ?? null,
    destino: destinoDeLaCondicion({ ...condicion, detalle: [d] }),
  }));
}

const TITULO_DE_LA_CONDICION: Record<string, string> = {
  dudas_bloqueantes: "Dudas que bloquean",
  correcciones_pendientes: "Correcciones pendientes",
  valorizacion_pendiente: "Valor de cada rubro",
  secciones_sin_validar: "Secciones redactadas por IA",
  secciones_desactualizadas: "Secciones desactualizadas",
  secciones_con_errores_de_verificacion: "Secciones con errores en sus citas",
  items_sin_modo: "Ítems sin modo de producción",
  items_con_recurso_pendiente: "Ítems con recurso pendiente",
  items_con_contenido_pendiente: "Ítems sin texto",
  observaciones_graves: "Observaciones graves de calidad",
  sin_revision_de_calidad: "Revisión de calidad",
  revision_de_calidad_desactualizada: "Revisión de calidad",
};

/**
 * El checklist de «Antes de emitir»: una fila roja por cada bloqueo (con el
 * mensaje y la cantidad del servidor), una fila de advertencias que siempre
 * está (ámbar si hay, verde si no) y, sin bloqueos, una fila verde que lo dice.
 */
export function filasDelChecklist(condiciones: CondicionesDeEmision): FilaDelChecklist[] {
  const filas: FilaDelChecklist[] = condiciones.bloqueos.map((c) => ({
    clave: c.codigo,
    titulo: TITULO_DE_LA_CONDICION[c.codigo] ?? c.codigo,
    detalle: c.mensaje,
    cantidad: c.cantidad,
    tono: "bloquea",
    icono: "✕",
    destino: destinoDeLaCondicion(c),
    elementos: elementosDe(c),
  }));

  if (condiciones.bloqueos.length === 0) {
    filas.push({
      clave: "listo",
      titulo: "Condiciones de emisión",
      detalle: "Todo resuelto",
      cantidad: 0,
      tono: "ok",
      icono: "✓",
      destino: null,
      elementos: [],
    });
  }

  const abiertas = condiciones.advertencias.reduce((suma, c) => suma + c.cantidad, 0);
  filas.push({
    clave: "advertencias",
    titulo: "Advertencias",
    detalle:
      abiertas > 0
        ? `${abiertas} ${abiertas === 1 ? "abierta: quedará anotada" : "abiertas: quedarán anotadas"}`
        : "Ninguna abierta",
    cantidad: abiertas,
    tono: abiertas > 0 ? "aviso" : "ok",
    icono: abiertas > 0 ? "!" : "✓",
    destino: condiciones.advertencias.length > 0 ? destinoDeLaCondicion(condiciones.advertencias[0]) : null,
    elementos: condiciones.advertencias.flatMap(elementosDe),
  });

  return filas;
}

export const TITULO_DEL_BOTON_BLOQUEADO = "Resuelve lo marcado en rojo para emitir";

export function textoDelBotonDeEmitir(emisiones: Emision[]): string {
  return emisiones.length > 0 ? "Emitir nueva versión" : "Emitir informe";
}

export const etiquetaDeVersion = (version: number) => `v${version}`;

/** «25-09-2026 15:40 · Ana Tasadora · Agrícola estándar», con la hora de Chile. */
export function metaDeLaVersion(emision: Emision): string {
  return [formatearFechaYHora(emision.emitido_en), emision.autor?.nombre, emision.formato?.nombre]
    .filter((x): x is string => Boolean(x))
    .join(" · ");
}

/**
 * La etiqueta de la vista previa. «EMITIDO vN» solo mientras lo emitido sigue
 * siendo el documento de hoy: si algo cambió después de la última emisión
 * (`ultimo_cambio` del servidor), lo que se vería ya es otro papel.
 */
export function estadoDeLaVistaPrevia(
  condiciones: CondicionesDeEmision | undefined,
  emisiones: Emision[],
): { texto: string; tono: "ok" | "aviso" } {
  const ultima = emisiones[0];
  if (!ultima) return { texto: "BORRADOR", tono: "aviso" };

  const cambio = condiciones?.ultimo_cambio ?? null;
  const vigente = cambio === null || new Date(cambio).getTime() <= new Date(ultima.emitido_en).getTime();

  return vigente
    ? { texto: `EMITIDO ${etiquetaDeVersion(ultima.version)}`, tono: "ok" }
    : { texto: "BORRADOR", tono: "aviso" };
}

/** La tarjeta de la fase 4 en el carril, de las mismas condiciones que el checklist. */
export function estadoDeLaFase4(
  condiciones: CondicionesDeEmision | undefined,
  emisiones: Emision[] = [],
): { tono: TonoDelCarril; texto: string } {
  if (condiciones === undefined) return { tono: "reposo", texto: "Sin cargar" };

  if (condiciones.bloqueos.length > 0) {
    return {
      tono: "bloquea",
      texto: `${plural(condiciones.bloqueos.length, "condición", "condiciones")} por resolver`,
    };
  }

  const vigente = estadoDeLaVistaPrevia(condiciones, emisiones);
  if (vigente.tono === "ok") return { tono: "ok", texto: `Emitido ${etiquetaDeVersion(emisiones[0].version)}` };

  return { tono: "ok", texto: "Listo para emitir" };
}

export type ErrorDeEmision =
  | { tipo: "bloqueada"; bloqueos: CondicionDeEmision[] }
  | { tipo: "otro"; mensaje: string };

/** 409 con lista de bloqueos → la lista; 503 y el resto → su mensaje legible. */
export function leerErrorDeEmision(error: unknown): ErrorDeEmision {
  if (error instanceof ApiError) {
    try {
      const cuerpo = JSON.parse(error.body) as { codigo?: string; bloqueos?: CondicionDeEmision[] };
      if (error.status === 409 && cuerpo.codigo === "emision_bloqueada" && Array.isArray(cuerpo.bloqueos)) {
        return { tipo: "bloqueada", bloqueos: cuerpo.bloqueos };
      }
    } catch {
      // No era JSON: cae al mensaje genérico.
    }
  }

  return { tipo: "otro", mensaje: getApiErrorMessage(error, "No pudimos emitir el informe. Inténtalo de nuevo.") };
}

export interface GruposDeObservaciones {
  graves: ObservacionDeCalidad[];
  advierten: ObservacionDeCalidad[];
  resueltas: ObservacionDeCalidad[];
  yaNoAparecen: ObservacionDeCalidad[];
}

export function agruparObservaciones(observaciones: ObservacionDeCalidad[]): GruposDeObservaciones {
  return {
    graves: observaciones.filter((o) => o.estado === "abierta" && o.gravedad === "grave"),
    advierten: observaciones.filter((o) => o.estado === "abierta" && o.gravedad === "advierte"),
    resueltas: observaciones.filter((o) => o.estado === "corregida" || o.estado === "descartada"),
    yaNoAparecen: observaciones.filter((o) => o.estado === "ya_no_aparece"),
  };
}

/** Cuándo corrió la revisión y si el documento cambió después (entonces no vale). */
export function frescuraDeLaRevision(condiciones: CondicionesDeEmision): { texto: string; desactualizada: boolean } {
  const revision = condiciones.ultima_revision_de_calidad;
  if (revision === null) return { texto: "Todavía no se ha corrido", desactualizada: true };

  const cambio = condiciones.ultimo_cambio;
  const desactualizada = cambio !== null && new Date(cambio).getTime() > new Date(revision).getTime();
  const cuando = formatearFechaYHora(revision);

  return {
    texto: `Última revisión: ${cuando} · ${desactualizada ? "el documento cambió después" : "al día"}`,
    desactualizada,
  };
}

export const ETIQUETA_DE_ORIGEN: Record<OrigenDeObservacion, string> = {
  determinista: "Regla del sistema",
  ia: "Revisión con IA",
};
